/**
 * IncomeProjectionPage — Household dividend & interest income projection.
 *
 * Route: /app/income-projection
 *
 * Shows total projected annual passive income, split between dividends and
 * interest, rolled up by wrapper/owner/account, plus a forward projection
 * chart with adjustable dividend growth % and horizon.
 *
 * Complements IncomePage (realized income events) — this is the forward-
 * looking yield view.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Banknote, TrendingUp, Coins, Sparkles, RefreshCw, PieChart as PieIcon,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts'
import { api } from '@/lib/api'
import IncomeTabs from '@/components/ui/IncomeTabs'

// ── Types ────────────────────────────────────────────────────────────────────
interface WrapperRow {
  wrapper: string
  value_gbp: number
  income_gbp: number
  dividend_income_gbp: number
  interest_income_gbp: number
  yield_pct: number
  pct_of_total: number
}

interface OwnerRow {
  owner: string
  value_gbp: number
  income_gbp: number
  dividend_income_gbp: number
  interest_income_gbp: number
  yield_pct: number
  pct_of_total: number
}

interface AccountRow {
  account_id: number
  account_name: string
  owner: string
  wrapper: string
  provider: string
  discretionary: boolean
  value_gbp: number
  income_gbp: number
  dividend_income_gbp: number
  interest_income_gbp: number
  yield_pct: number
  pct_of_total: number
}

interface Contributor {
  name: string
  wrapper: string
  owner: string
  income_gbp: number
  yield_pct: number
  pct_of_total: number
}

interface HoldingRow {
  holding_id: number
  account_id: number
  account_name: string
  wrapper: string
  owner: string
  name: string
  symbol: string | null
  asset_type: string
  sector: string | null
  region: string | null
  value_gbp: number
  yield_pct: number
  annual_income_gbp: number
  income_category: string
}

interface ProjectionPoint {
  year: number
  annual_dividend_gbp: number
  annual_interest_gbp: number
  annual_income_gbp: number
  cumulative_gbp: number
}

interface ProjectionRealPoint {
  year: number
  annual_income_gbp: number
  cumulative_gbp: number
}

interface IncomeProjection {
  total_annual_income_gbp: number
  total_annual_dividends_gbp: number
  total_annual_interest_gbp: number
  yield_on_portfolio_pct: number
  portfolio_value_eligible_gbp: number
  monthly_income_gbp: number
  by_wrapper: WrapperRow[]
  by_owner: OwnerRow[]
  by_account: AccountRow[]
  top_contributors: Contributor[]
  per_holding: HoldingRow[]
  projection: ProjectionPoint[]
  projection_real: ProjectionRealPoint[]
  assumptions: {
    years_out: number
    dividend_growth_pct: number
    interest_held_flat: boolean
    inflation_pct_for_real: number
    include_interest: boolean
    premium_bonds_yield_pct: number
    broker_cash_yields: Record<string, number>
    crypto_staking_yields: Record<string, number>
  }
  tax_treatment_note: string
}

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtCompact = (n: number) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

const fmtPct = (n: number) => `${n.toFixed(2)}%`

// ── Page ─────────────────────────────────────────────────────────────────────
export default function IncomeProjectionPage() {
  const [yearsOut, setYearsOut] = useState(20)
  const [divGrowth, setDivGrowth] = useState(3.0)
  const [includeInterest, setIncludeInterest] = useState(true)

  const [data, setData] = useState<IncomeProjection | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProjection = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<IncomeProjection>('/api/income/projection', {
        params: {
          years_out: yearsOut,
          dividend_growth_pct: divGrowth,
          include_interest: includeInterest,
        },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load income projection')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchProjection, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsOut, divGrowth, includeInterest])

  // Merge nominal and real projection rows
  const chartData = useMemo(() => {
    if (!data) return []
    return data.projection.map((p, i) => ({
      year: p.year,
      nominal: p.annual_income_gbp,
      real: data.projection_real[i]?.annual_income_gbp ?? 0,
      dividend: p.annual_dividend_gbp,
      interest: p.annual_interest_gbp,
    }))
  }, [data])

  const contributorChartData = useMemo(() => {
    if (!data) return []
    return [...data.top_contributors]
      .slice(0, 10)
      .reverse()
      .map(c => ({ name: c.name, income: c.income_gbp, yield_pct: c.yield_pct }))
  }, [data])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Banknote size={20} className="text-emerald" aria-hidden />
            Income Projection
          </h1>
          <p className="text-sm text-text-secondary">
            Dividends, interest, staking and cash yield across the whole household —
            projected forward to retirement.
          </p>
        </div>
        <button type="button" onClick={fetchProjection} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <IncomeTabs />

      {error && (
        <div className="card p-4 border border-rose/40 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* KPI cards */}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card card-hover">
            <p className="stat-label">Total annual income</p>
            <p className="stat-hero text-emerald-light">{fmt(data.total_annual_income_gbp)}</p>
            <p className="text-xs text-text-muted">
              ≈ {fmt(data.monthly_income_gbp)}/month
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Yield on portfolio</p>
            <p className="stat-value text-blue-light">{fmtPct(data.yield_on_portfolio_pct)}</p>
            <p className="text-xs text-text-muted">
              on {fmt(data.portfolio_value_eligible_gbp)} eligible
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Dividend income</p>
            <p className="stat-value">{fmt(data.total_annual_dividends_gbp)}</p>
            <p className="text-xs text-text-muted">equity + fund distributions</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Interest & cash</p>
            <p className="stat-value">{fmt(data.total_annual_interest_gbp)}</p>
            <p className="text-xs text-text-muted">bonds, gilts, cash, staking</p>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Projection assumptions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberSlider
            label="Dividend growth p.a."
            value={divGrowth}
            onChange={setDivGrowth}
            min={0}
            max={8}
            step={0.25}
            format={(v) => `${v.toFixed(2)}%`}
          />
          <NumberSlider
            label="Projection horizon"
            value={yearsOut}
            onChange={setYearsOut}
            min={1}
            max={40}
            step={1}
            format={(v) => `${v} yrs`}
          />
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
              Interest / cash yield
            </label>
            <button
              type="button"
              onClick={() => setIncludeInterest(!includeInterest)}
              className="btn-secondary text-xs w-full justify-center"
            >
              {includeInterest ? 'Included' : 'Excluded'} — click to toggle
            </button>
          </div>
        </div>
      </div>

      {/* Projection chart */}
      {data && chartData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald" aria-hidden />
            {yearsOut}-year projected annual income
          </h2>
          <p className="text-xs text-text-muted mb-4">
            Dividends compound at {divGrowth.toFixed(2)}% p.a.
            Interest held flat nominally. Real terms deflates at {data.assumptions.inflation_pct_for_real}% inflation.
          </p>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis
                  dataKey="year"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={(v) => `Y${v}`}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={fmtCompact}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0b1220', border: '1px solid #1f2937', fontSize: '12px', color: 'rgb(var(--text-primary))'}}
                  labelFormatter={(v) => `Year ${v}`}
                  formatter={(v: number, name: string) => [fmt(v), name]}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line
                  type="monotone"
                  dataKey="nominal"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={false}
                  name="Nominal"
                />
                <Line
                  type="monotone"
                  dataKey="real"
                  stroke="#60a5fa"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                  name="Real (today's £)"
                />
                <Line
                  type="monotone"
                  dataKey="dividend"
                  stroke="#a78bfa"
                  strokeWidth={1.5}
                  dot={false}
                  name="Dividend only"
                />
                <Line
                  type="monotone"
                  dataKey="interest"
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  dot={false}
                  name="Interest only"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top contributors bar chart */}
      {data && contributorChartData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Coins size={14} className="text-amber" aria-hidden />
            Top 10 income contributors
          </h2>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={contributorChartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 140, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={fmtCompact}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#94a3b8"
                  fontSize={11}
                  width={140}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0b1220', border: '1px solid #1f2937', fontSize: '12px', color: 'rgb(var(--text-primary))'}}
                  formatter={(v: number) => [fmt(v), 'Annual income']}
                />
                <Bar dataKey="income" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Wrapper table */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <PieIcon size={14} className="text-blue-light" aria-hidden />
            Income by wrapper
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Wrapper</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Dividends</th>
                  <th className="!text-right">Interest</th>
                  <th className="!text-right">Total income</th>
                  <th className="!text-right">Yield</th>
                  <th className="!text-right">% of total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_wrapper.map(r => (
                  <tr key={r.wrapper}>
                    <td className="py-2 pr-3 text-text-primary font-semibold">{r.wrapper}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.value_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.dividend_income_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.interest_income_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-emerald-light font-semibold">{fmt(r.income_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmtPct(r.yield_pct)}</td>
                    <td className="py-2 text-right text-text-muted">{r.pct_of_total.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-text-muted mt-4 leading-relaxed">
            {data.tax_treatment_note}
          </p>
        </div>
      )}

      {/* Owner roll-up */}
      {data && data.by_owner.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">Income by owner</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Dividends</th>
                  <th className="!text-right">Interest</th>
                  <th className="!text-right">Total income</th>
                  <th className="!text-right">Yield</th>
                </tr>
              </thead>
              <tbody>
                {data.by_owner.map(r => (
                  <tr key={r.owner}>
                    <td className="py-2 pr-3 text-text-primary font-semibold">{r.owner}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.value_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.dividend_income_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.interest_income_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-emerald-light font-semibold">{fmt(r.income_gbp)}</td>
                    <td className="py-2 text-right text-text-secondary">{fmtPct(r.yield_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Account drilldown */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">Income by account</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Wrapper</th>
                  <th>Owner</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Income</th>
                  <th className="!text-right">Yield</th>
                </tr>
              </thead>
              <tbody>
                {data.by_account.map(r => (
                  <tr key={r.account_id}>
                    <td className="py-2 pr-3 text-text-primary">
                      {r.account_name}
                      {!r.discretionary && (
                        <span className="ml-2 text-[10px] uppercase text-amber">managed</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-text-secondary">{r.wrapper}</td>
                    <td className="py-2 pr-3 text-text-secondary">{r.owner}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{fmt(r.value_gbp)}</td>
                    <td className="py-2 pr-3 text-right text-emerald-light font-semibold">{fmt(r.income_gbp)}</td>
                    <td className="py-2 text-right text-text-secondary">{fmtPct(r.yield_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Methodology */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Yield methodology &amp; assumptions
        </summary>
        <div className="mt-3 space-y-2 leading-relaxed">
          <p>
            <span className="text-text-primary font-semibold">Equity / fund / ETF:</span> known
            name overrides first (e.g. BATS 8%, L&amp;G 8%, Shell 4%, Microsoft 0.7%), then
            sector/region defaults — UK Equity 3.8%, US Equity 1.4%, Global Equity 2.0%,
            FTSE 100 3.5%, Technology 0.7%, Financials 4%, etc.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Bonds / gilts / bond funds:</span>
            {' '}Government Bonds 4.0%, Investment Grade 4.5%, High Yield 6.5%, EM Debt 5.8%.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Cash:</span> broker-specific —
            AJ Bell 2.85%, Hargreaves Lansdown 2.50%, IG Index 0%, Coinbase/manual 0%.
            Premium Bonds 4.40% (NS&amp;I prize fund equivalent).
          </p>
          <p>
            <span className="text-text-primary font-semibold">Crypto:</span> BTC 0%, ETH 3.2%
            (100% staked), SOL ~6.4% (99% staked), LTC 3.5%, all other alts 0%.
          </p>
          <p className="pt-2 text-text-muted">
            The nominal projection compounds the dividend side at the growth rate you set above.
            The interest side is held flat (rate forecasting is too noisy to pretend otherwise).
            Real terms additionally deflates by {data?.assumptions.inflation_pct_for_real ?? 2.5}% inflation.
            Real estate and liabilities are excluded from the income maths. This is a forward
            estimate — actual distributions will differ.
          </p>
        </div>
      </details>
    </div>
  )
}

// ── Slider input ─────────────────────────────────────────────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-emerald"
        aria-label={label}
      />
    </div>
  )
}
